import { ContributionMarginYearBreakdown } from "models";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";
import { ResilienceTierKey, getResilienceTier } from "pages/app/scenario-analysis/utils/resilienceScorecard";

/** Cumulative margin difference against the baseline at the end of one projection year. */
export interface CumulativeGapYear {
  year: number;
  /** Subject minus baseline, summed over years 1..year, in the farm's currency per ha. */
  gap: number;
}

const marginForYear = (rows: ContributionMarginYearBreakdown[], year: number): number | null => {
  const row = rows.find((candidate) => candidate.year === year);
  return row ? bandAvg(row.contributionMargin?.total) : null;
};

/** Running cumulative gap for years 1..projectionYears; a year counts only when both sides reported it. */
export const cumulativeGapByYear = (
  subjectRows: ContributionMarginYearBreakdown[],
  baselineRows: ContributionMarginYearBreakdown[],
  projectionYears: number
): CumulativeGapYear[] => {
  const gaps: CumulativeGapYear[] = [];
  let subjectTotal = 0;
  let baselineTotal = 0;

  for (let year = 1; year <= projectionYears; year += 1) {
    const subjectMargin = marginForYear(subjectRows, year);
    const baselineMargin = marginForYear(baselineRows, year);
    if (subjectMargin !== null && baselineMargin !== null) {
      subjectTotal += subjectMargin;
      baselineTotal += baselineMargin;
      gaps.push({ year, gap: subjectTotal - baselineTotal });
    }
  }

  return gaps;
};

export interface BreakEven {
  /** The year after which the scenario never falls behind again; 1 when it never does, null when it stays behind. */
  year: number | null;
  /** Deepest the cumulative gap goes across the whole horizon; 0 when it is never behind. */
  dip: number;
}

/** Last year behind, plus one: the gap can cross zero more than once, so this is not the first year ahead. */
export const findBreakEven = (gaps: CumulativeGapYear[]): BreakEven => {
  const behind = gaps.filter((entry) => entry.gap < 0);
  if (gaps.length === 0 || behind.length === 0) return { year: 1, dip: 0 };

  const lastBehindYear = behind[behind.length - 1].year;
  const finalYear = gaps[gaps.length - 1].year;
  // Two dips report the worse one, so the depth is taken across the whole horizon.
  const dip = Math.min(...behind.map((entry) => entry.gap));

  return { year: lastBehindYear === finalYear ? null : lastBehindYear + 1, dip };
};

export type GapTone = "positive" | "negative";

export interface GapBand {
  tone: GapTone;
  /** Projection years the band spans, which is its share of the bar. */
  years: number;
  startYear: number;
  endYear: number;
}

/** Contiguous years of the same sign merge, so one crossing reads as two bands and a wobble as several. */
export const toGapBands = (gaps: CumulativeGapYear[]): GapBand[] => {
  const bands: GapBand[] = [];

  gaps.forEach((entry) => {
    const tone: GapTone = entry.gap < 0 ? "negative" : "positive";
    const current = bands[bands.length - 1];
    if (current && current.tone === tone) {
      current.years += 1;
      current.endYear = entry.year;
      return;
    }
    bands.push({ tone, years: 1, startYear: entry.year, endYear: entry.year });
  });

  return bands;
};

export type ResilienceBandKey = "strong" | "good" | "moderate" | "weak";

/** The card's four bands are the scorecard's five tiers relabelled, so one threshold table serves both. */
const BAND_BY_TIER: Record<ResilienceTierKey, ResilienceBandKey> = {
  strong: "strong",
  good: "good",
  developing: "moderate",
  needsAttention: "weak",
  critical: "weak",
};

export const getResilienceCardBand = (score: number): ResilienceBandKey => BAND_BY_TIER[getResilienceTier(score).key];

/** Sorted multiset of main crop ids, so rotation order and repeats are both accounted for. */
const mainCropMultiset = (scenario: ScenarioData): string[] =>
  scenario.years
    .filter((year) => !!year.crop)
    .map((year) => year.crop)
    .sort();

/** Whether the subject grows the same crops as the baseline; a yield comparison across different crops is not like-for-like. */
export const cropsMatchBaseline = (baseline: ScenarioData | null, subject: ScenarioData | null): boolean => {
  if (!baseline || !subject) return true;
  const baselineCrops = mainCropMultiset(baseline);
  const subjectCrops = mainCropMultiset(subject);
  return baselineCrops.length === subjectCrops.length && baselineCrops.every((cropId, index) => cropId === subjectCrops[index]);
};
